import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from 'react-query';
import styled, { css, keyframes } from 'styled-components';
import moment from 'moment';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { ReactComponent as EmailIcon } from '@ubeya/shared-web/assets/email.svg';
import {
  Avatar as BaseAvatar,
  Button as BasicButton,
  card,
  CircleIcon as BaseCircleIcon,
  FlexColumn,
  FlexMiddle,
  FlexSpace,
  Header2SemiBold,
  PromptClick,
  Small,
  TimeAgoRenderer,
  SmallBody as BaseSmallBody,
  Loader,
  ContextMenu,
  Tooltip,
  useModal,
  SemiExtraSmall,
  BodySemiBold
} from '@ubeya/shared-web/components';
import privileges from '@ubeya/shared-web/privileges';
import useOnClickOutside from '@ubeya/shared-web/hooks/useOnClickOutside';
import usePortal from '@ubeya/shared-web/hooks/usePortal';
import { ReactComponent as BaseChatIcon } from '@ubeya/shared-web/assets/chat-blue.svg';
import { ReactComponent as BaseInviteIcon } from '@ubeya/shared-web/assets/app-confirmation.svg';
import { ReactComponent as FilledChatIcon } from '@ubeya/shared-web/assets/ubeya-chat-fill.svg';
import { ReactComponent as BalloonIcon } from '@ubeya/shared-web/assets/balloon.svg';
import { ReactComponent as BaseDeleteIcon } from '@ubeya/shared-web/assets/delete.svg';
import { ReactComponent as BasePlusIcon } from '@ubeya/shared-web/assets/help-desk/plus.svg';
import { ReactComponent as BasePhoneMenuIcon } from '@ubeya/shared-web/assets/chat.svg';
import { ReactComponent as AppMenuIcon } from '@ubeya/shared-web/assets/app.svg';
import { ReactComponent as CloseIcon } from '@ubeya/shared-web/assets/close-big.svg';
import { ReactComponent as IdIcon } from '@ubeya/shared-web/assets/id.svg';
import { ReactComponent as BasePhoneIcon } from '@ubeya/shared-web/assets/phone-big.svg';
import { ReactComponent as PushNotificationIconBase } from '@ubeya/shared-web/assets/app-confirmation.svg';
import { ReactComponent as EmployeeIcon } from '@ubeya/shared-web/assets/employee.svg';
import { ReactComponent as WhatsappIcon } from '@ubeya/shared-web/assets/whatsapp.svg';
import { ReactComponent as ErrorIcon } from '@ubeya/shared-web/assets/error.svg';
import { maxMedia } from '@ubeya/shared-web/style';
import useChatIframe from '@ubeya/shared-web/hooks/useChatIframe';
import useCRM from '@ubeya/shared/hooks/account/useCRM';
import useFields, { FieldTypes } from '@ubeya/shared/hooks/account/useFields';
import usePositions from '@ubeya/shared/hooks/account/usePositions';
import useBranches from '@ubeya/shared/hooks/account/useBranches';
import useModules from '@ubeya/shared/hooks/account/useModules';
import useDepartments from '@ubeya/shared/hooks/account/useDepartments';
import useEmploymentTypes from '@ubeya/shared/hooks/generic/useEmploymentTypes';
import useFormattingMap from '@ubeya/shared-web/hooks/useFormattingMap';
import useAccount from '@ubeya/shared/hooks/account/useAccount';
import useEmployee from '@ubeya/shared/hooks/employees/useEmployee';
import PrivilegesContext from '@ubeya/shared-web/contexts/PrivilegesContext';
import useLocations from '@ubeya/shared/hooks/account/useLocations';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import useLabels from '@ubeya/shared/hooks/account/useLabels';
import { API_DATE_FORMAT } from '@ubeya/shared/constants';
import { getHelpDeskEmployeeTicketsQueryKey } from '@ubeya/shared/hooks/account/useHelpDeskEmployeeTickets';
import { isSeniorityModelPools, isSeniorityModelRankingBased } from '@ubeya/shared/utils/accounts';
import useEmployeeComments from '@ubeya/shared/hooks/account/useEmployeeComments';
import { SORT_BY_DATE_AND_TIME } from '@ubeya/shared/utils/sorting';
import useEmployeeCompliance from '@ubeya/shared/hooks/account/useEmployeeCompliance';
import { getEmployeeComplianceDataStatus } from '@ubeya/shared-web/utils/employee';
import useUser from '@ubeya/shared/hooks/auth/useUser';
import useAccountConfig from '@ubeya/shared/hooks/account/useAccountConfig';
import { formatLevelOption, getEmployeeDisplayName } from '@ubeya/shared-web/utils/employee';
import { findEffectiveWage } from '@ubeya/shared-web/utils/positions';
import { groupWages } from 'shared/utils/wages';
import EmployeeKpis from '../EmployeeKpis';
import { isDocumentFieldValue } from './profileSidebarUtils';
import BaseComment from './Comment';
import { DetailsBlockChip, DetailBlockText, DetailsBlockWrapper, Chips, DetailRow } from './DetailBlock';
import { PhotoGallery } from './PhotoGallery';
import Accordion from './Accordion';
import DeleteEmployeeModal from 'employees/components/DeleteEmployeeModal';
import DeactivateEmployeeModal from 'employees/components/DeactivateEmployeeModal';
import CreateTicketModal from 'helpDesk/components/CreateTicket/CreateTicketModal';
import TicketsHistorySection from 'helpDesk/components/TicketsHistorySection';
import { useHelpDeskAnalytics } from 'helpDesk/helpDeskAnalytics';
import useOpenTicketInSidePanel from 'helpDesk/useOpenTicketInSidePanel';

const Wrapper = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  left: 0;
  bottom: 0;
  background: rgba(19, 21, 41, 0.3);
  z-index: ${({ $isSingleView }) => ($isSingleView ? 10 : 5)};
`;

const NameRow = styled.div`
  display: flex;
  position: relative;
  align-items: center;
  margin-bottom: 16px;
`;

const jumpIn = keyframes`
  0%{transform: translateX(100%);}
  100%{transform: translateX(0);}
`;

//Icons styling

const icon = css`
  height: 22px;
  width: 22px;
`;

const CircleIcon = styled(BaseCircleIcon)`
  padding: 0;
`;

const ChatIcon = styled(BaseChatIcon)`
  ${icon},
  path {
    fill: ${({ theme, $isDisabled }) => ($isDisabled ? theme.colors.disabled : theme.colors.grey4)};
  }
`;

const ActivateIcon = styled(EmployeeIcon)`
  path {
    fill: ${({ theme }) => theme.colors.greenPositive};
    stroke: ${({ theme }) => theme.colors.greenPositive};
  }
`;

const InActivateIcon = styled(EmployeeIcon)`
  path {
    fill: ${({ theme }) => theme.colors.grey4};
    stroke: ${({ theme }) => theme.colors.grey4};
  }
`;

const SmallBody = styled(BaseSmallBody)`
  color: ${({ theme, $isDisabled }) => ($isDisabled ? theme.colors.disabled : theme.colors.grey4)};
`;

const InviteIcon = styled(BaseInviteIcon)`
  ${icon}
`;

const PhoneIcon = styled(BasePhoneIcon)`
  height: 15px;
  width: 15px;
  stroke-width: 1;
`;

const PhoneMenuIconSmall = styled(BasePhoneMenuIcon)`
  height: 15px;
  width: 15px;
`;

//Buttons styling
const CloseButton = styled(CircleIcon)`
  position: fixed;
  right: 15px;
  top: 15px;
  z-index: 1;
`;

const DeleteIcon = styled(BaseDeleteIcon)`
  width: 17px;
  height: 17px;
`;

const CreateTicketIcon = styled(BasePlusIcon)`
  width: 20px;
  height: 20px;
  color: ${({ theme }) => theme.colors.grey4};
`;

const Button = styled(BasicButton)`
  width: 160px;
  border-radius: 6px;
  margin-bottom: 16px;
  margin-top: 8px;
`;

const Container = styled(FlexColumn)`
  ${card};
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  max-width: 25%;
  ${maxMedia.mobile`max-width: 100%;`};
  border-radius: 8px 0 0 8px;
  animation: ${jumpIn} 300ms;
  overflow: auto;
`;

const ShortInfo = styled(FlexColumn)`
  align-items: center;
  padding: 30px 0 0;
`;

const Avatar = styled(BaseAvatar)`
  margin-bottom: 24px;
  font-size: 66rem;
`;

const Name = styled(Header2SemiBold)`
  text-align: center;
`;

const InfoRow = styled(FlexMiddle)`
  margin-bottom: 16px;
  gap: 8px;
`;

const RatingRow = styled(FlexMiddle)`
  justify-content: center;
  margin-bottom: 16px;
`;

const LastActivity = styled(Small)`
  color: ${({ theme }) => theme.colors.black};

  & * {
    color: ${({ theme }) => theme.colors.black};
  }
`;

const ActionsRow = styled(FlexSpace)`
  display: flex;
  justify-content: center;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 32px;
  margin-bottom: 24px;
`;

const ActionCell = styled(FlexColumn)`
  align-items: center;
`;

const PushNotificationIcon = styled(PushNotificationIconBase)`
  width: 20px;
  height: 20px;
`;

const Comment = styled(BaseComment)`
  .employee-comments {
    border: 0 none;
  }
`;

const PendingWarning = styled.div`
  margin-left: 5px;
`;

const CircleChip = styled(ErrorIcon)`
  width: 16px;
  height: 16px;
  min-width: 16px;
  min-height: 16px;
  margin-left: 4px;
  margin-top: 4px;
`;

const DetailBlockTitle = styled(BodySemiBold)``;

const DetailBlockSubText = styled(SemiExtraSmall)``;

const ProfileSidebar = ({
  image,
  firstName,
  lastName,
  id: employeeId,
  employeeIdField,
  isActive,
  isDeleted,
  rating,
  jobsRatingCount,
  email,
  notes,
  phone,
  userId,
  isPushNotification,
  isPending,
  phoneSecondary,
  lastActivity,
  positions,
  branches,
  departmentId,
  employmentTypeId,
  fields: fieldsValues,
  positionWages,
  wages,
  isLoading,
  inviteEmployee,
  onClose,
  onEdit,
  employeeFields,
  jobSummary,
  level,
  photos = [],
  isSingleView = false,
  extraSections = [],
  defaultOpenSections,
  inline = false
}) => {
  const ref = useRef();
  const { Portal } = usePortal({ enabled: true, portalId: 'modal' });
  const { accountId } = useAccount();
  const queryClient = useQueryClient();
  const { trackCreateTicketOpened } = useHelpDeskAnalytics();
  const { account: accountConfig } = useAccountConfig();
  const { accounts } = useUser();

  const { isRestrictedFromEmployees } = accounts.find(({ id }) => id === accountId) || {};

  const { modules } = useModules();

  const [deleteEmployeeModal, setDeleteEmployeeModal] = useState(false);
  const [deactivateEmployeeModal, setDeactivateEmployeeModal] = useState(false);
  const [createTicketModal, setCreateTicketModal] = useState(false);
  const [ticketsHistoryCreateModal, setTicketsHistoryCreateModal] = useState(false);
  const [isPhotoGalleryOpen, setIsPhotoGalleryOpen] = useState(false);
  const [isDocumentPreviewOpen, setIsDocumentPreviewOpen] = useState(false);

  const {
    dateFormat,
    timeFormat,
    t,
    isAccountNetworkAccessSupplier,
    enableContractFeature,
    isShowLastNameFirst = false
  } = useContext(ConfigContext);

  useOnClickOutside(
    ref,
    onClose,
    !inline &&
      !deleteEmployeeModal &&
      !isPhotoGalleryOpen &&
      !deactivateEmployeeModal &&
      !isDocumentPreviewOpen &&
      !createTicketModal &&
      !ticketsHistoryCreateModal
  );

  const lastActivityProps = useMemo(() => ({ rowData: { lastActivity }, dataKey: 'lastActivity' }), [lastActivity]);

  const { fields, mappedFieldsValue } = useFields();
  const { mappedClientBranches } = useCRM();
  const { mappedPositions } = usePositions();
  const { mappedBranches } = useBranches();
  const { mappedLabels, labelOptions } = useLabels();
  const { mappedLocations } = useLocations();
  const { mappedDepartments } = useDepartments();
  const { mappedEmploymentTypes } = useEmploymentTypes();
  const myPrivileges = useContext(PrivilegesContext);
  const canViewSensitiveData = !!myPrivileges[privileges.SENSITIVE_DATA_ACCESS];
  const formattingMap = useFormattingMap();
  const { attachModal, detachModal } = useModal();

  const showLabel = useMemo(() => labelOptions?.length > 1, [labelOptions?.length]);

  const existsValueInField = (item) => !!item?.value && (!!item.value?.title || Array.isArray(item.value));

  const getField = useCallback(
    (slug) => {
      const currentField = fields.find((field) => field.slug === slug || field.name === slug);
      if (!currentField) return null;

      const cannotViewSensitiveValue = currentField.isSensitive && !canViewSensitiveData;

      if (cannotViewSensitiveValue) return null;

      if (!fieldsValues?.[`field-${currentField.id}`]) return null;

      const rawValue = fieldsValues[`field-${currentField.id}`];

      if (currentField?.fieldTypeId === FieldTypes.FIELD_TYPE_DOCUMENT) {
        if (isDocumentFieldValue(rawValue)) {
          return {
            label: t(currentField.name),
            value: rawValue.map((doc) => ({ title: doc?.title || t('document'), link: doc?.link })),
            type: currentField?.fieldTypeId
          };
        }
        return null;
      }

      const title = mappedFieldsValue[currentField.id]?.(rawValue);
      return {
        label: t(currentField.name),
        value: { title: title ?? '' },
        type: currentField?.fieldTypeId
      };
    },
    [canViewSensitiveData, fields, fieldsValues, t, mappedFieldsValue]
  );

  const { data: employeeCommentsData } = useEmployeeComments({
    employeeId
  });

  const { data: employeeComplianceData } = useEmployeeCompliance({
    employeeId
  });

  const employeeComplianceDataStatus = useMemo(
    () =>
      getEmployeeComplianceDataStatus({
        employeeComplianceData,
        mappedPositions,
        employeeFields,
        t
      }),
    [employeeComplianceData, employeeFields, mappedPositions, t]
  );

  const timeSorter = SORT_BY_DATE_AND_TIME('createdAt');
  const employeeCommentsSortedData = useMemo(
    () => (employeeCommentsData || []).sort((a, b) => timeSorter(b, a))?.slice(0, 5),
    [employeeCommentsData, timeSorter]
  );

  const { openUserChat, hasViewChatPrivilege } = useChatIframe();

  const options = useMemo(
    () => [
      ...(hasViewChatPrivilege
        ? [
            {
              label: t('chat'),
              icon: FilledChatIcon,
              disabled: !userId,
              onClick: () => {
                onClose();
                openUserChat(userId, accountId);
              }
            }
          ]
        : []),
      {
        label: t('whatsapp'),
        disabled: !phone,
        icon: WhatsappIcon,
        onClick: () => window.open(`https://wa.me/${phone}`, '_blank')?.focus()
      },
      {
        label: t('iMessage'),
        disabled: !phone,
        icon: PhoneMenuIconSmall,
        onClick: () => window.open(`sms:${phone};`, '_blank')?.focus()
      },
      {
        label: t('call'),
        disabled: !phone,
        icon: PhoneIcon,
        onClick: () => window.open(`tel:${phone}`, '_blank')?.focus()
      }
    ],
    [accountId, hasViewChatPrivilege, openUserChat, userId, onClose, phone, t]
  );

  const details = useMemo(() => {
    const generalDetails = [getField('gender')];
    const birthdateField = getField('birthdate');

    if (birthdateField?.value?.title) {
      generalDetails.push({
        label: t('age'),
        value: { title: moment().diff(moment(birthdateField.value.title, dateFormat || 'DD/MM/YYYY'), 'years') }
      });
    }

    if (isSeniorityModelPools({ seniorityModel: accountConfig?.config?.seniorityModel }) && level) {
      generalDetails.push({
        label: t('level'),
        value: { title: formatLevelOption({ t, level }) }
      });
    }

    const rankingField = getField('seniority-ranking');
    if (isSeniorityModelRankingBased({ seniorityModel: accountConfig?.config?.seniorityModel }) && rankingField) {
      generalDetails.push(rankingField);
    }

    if (employeeIdField) {
      generalDetails.push({
        label: t('employeeId'),
        value: { title: employeeIdField }
      });
    }

    generalDetails.push({
      label: t('positions'),
      value: { title: (positions || []).map((position) => mappedPositions[position]?.label) }
    });

    if (notes) {
      generalDetails.push({
        label: t('notes'),
        value: { title: notes }
      });
    }

    const buildShiftsData = (payload) => {
      const {
        date,
        startTime,
        branchId,
        projectId,
        endTime = null,
        locationId = null,
        shiftLocationId = null,
        positionId = null,
        clientBranchId = null,
        labelId = null
      } = payload || {};
      const dateTime = `${moment(date).format(dateFormat)}
      (${formattingMap.startAndEndTime(startTime, endTime)})`;

      let locationItem = '';

      // shift location
      if (shiftLocationId && mappedLocations[shiftLocationId]) {
        locationItem = `${mappedLocations[shiftLocationId]?.name}`;
      }

      // project location
      if (locationId && mappedLocations[locationId] && locationId !== shiftLocationId) {
        locationItem += ` (${mappedLocations[locationId]?.name})`;
      }

      const position = [
        positionId && mappedPositions[positionId] ? `${mappedPositions[positionId]?.label}` : '',
        locationItem,
        clientBranchId && mappedClientBranches[clientBranchId]
          ? `${mappedClientBranches[clientBranchId]?.clientName}`
          : '',
        showLabel && labelId ? `${mappedLabels[labelId]?.title}` : ''
      ]
        .filter((str) => str.length > 0)
        .join(' - ');

      let link = `${accountId}/scheduling/branch/${branchId}/project/${projectId}`;
      return { dateTime, position, link };
    };

    const upcomingShifts = (jobSummary || [])
      .map((payload) => ({ ...payload, date: moment(payload.date) }))
      .filter(({ date }) => {
        const formattdDate = moment(date).format(API_DATE_FORMAT);
        const formattdCurrentDate = moment().format(API_DATE_FORMAT);

        return moment(formattdDate).isSameOrAfter(moment(formattdCurrentDate));
      })
      .sort(({ date: d1 }, { date: d2 }) => (d1.isBefore(d2) ? -1 : 1))
      .map((payload) => buildShiftsData(payload));

    const pastShifts = (jobSummary || [])
      .map((payload) => ({ ...payload, date: moment(payload.date) }))
      .filter(({ date }) => {
        const formattdDate = moment(date).format(API_DATE_FORMAT);
        const formattdCurrentDate = moment().format(API_DATE_FORMAT);

        return moment(formattdDate).isBefore(moment(formattdCurrentDate));
      })
      .sort(({ date: d1 }, { date: d2 }) => (d1.isBefore(d2) ? 1 : -1))
      .map((payload) => buildShiftsData(payload));

    const jobSummaryDetails = [
      {
        label: t('upcomingShifts'),
        value: upcomingShifts && upcomingShifts.length > 0 ? upcomingShifts : []
      },

      {
        label: t('pastShifts'),
        value: pastShifts && pastShifts.length > 0 ? pastShifts : []
      }
    ].filter((detail) => detail.value.length > 0);

    const personalInformation = [
      {
        label: t('phone'),
        value: { title: phone ? parsePhoneNumberFromString(`+${phone}`)?.formatNational?.() : '' },
        showContextMenu: true,
        Icon: AppMenuIcon,
        isInline: true
      },
      {
        label: t('secondaryPhone'),
        value: { title: phoneSecondary ? parsePhoneNumberFromString(`+${phoneSecondary}`)?.formatNational?.() : '' },
        Icon: AppMenuIcon,
        isInline: true
      },
      {
        label: t('email'),
        value: { title: email },
        Icon: EmailIcon,
        onClick: () => window.open(`mailto:${email}`, '_blank')?.focus()
      },

      {
        label: t('employeeIdField'),
        value: { title: employeeIdField },
        Icon: IdIcon
      },
      { ...getField('birthdate'), Icon: BalloonIcon },
      {
        label: t('notes'),
        value: { title: notes }
      }
    ].filter(existsValueInField);

    const employmentDetails = [
      { label: t('branches'), value: (branches || []).map((branch) => ({ title: mappedBranches[branch]?.name })) },
      { label: t('department'), value: { title: mappedDepartments[departmentId] } },
      ...(!enableContractFeature
        ? [{ label: t('employmentType'), value: { title: mappedEmploymentTypes[employmentTypeId]?.label } }]
        : []),
      getField('start-employment-date'),
      getField('end-employment-date')
    ].filter(existsValueInField);

    const otherFields = fields
      .filter(
        ({ slug }) =>
          ![
            'gender',
            'birthdate',
            'employee-id',
            'start-employment-date',
            'end-employment-date',
            'seniority-ranking'
          ].includes(slug)
      )
      .map(({ slug, name }) => getField(slug || name));

    const positionsHourlyWages = groupWages({ positions: positions ?? [], data: positionWages ?? [] }).map(
      ({ positionId, hourlyWage }) => ({
        label: mappedPositions[positionId]?.label,
        value: { title: formattingMap.currency(hourlyWage) }
      })
    );

    const baseHourlyWage = findEffectiveWage(wages)?.hourlyWage;
    const baseWageDetails =
      baseHourlyWage != null
        ? [{ label: t('baseWage'), value: { title: formattingMap.currency(baseHourlyWage, { includeZero: true }) } }]
        : [];

    return [
      { title: t('generalDetails'), details: generalDetails },
      ...(jobSummaryDetails.length > 0
        ? [
            {
              title: t('jobSummary'),
              details: {
                component: (
                  <>
                    {jobSummaryDetails.map((jobSummaryProp) => (
                      <DetailsBlockWrapper key={jobSummaryProp.label}>
                        <DetailBlockText>{jobSummaryProp.label}</DetailBlockText>
                        <Chips>
                          {(jobSummaryProp?.value || []).map((item, index) => (
                            <DetailsBlockChip
                              $disableClick={!item.link}
                              onClick={() => item?.link && window.open(item.link, '_blank')}
                              key={index}
                              $isColored>
                              <DetailBlockSubText>{item.dateTime}</DetailBlockSubText>
                              <DetailBlockTitle>{item.position}</DetailBlockTitle>
                            </DetailsBlockChip>
                          ))}
                        </Chips>
                      </DetailsBlockWrapper>
                    ))}
                  </>
                )
              }
            }
          ]
        : []),
      ...(photos?.length > 0
        ? [
            {
              title: t('photos'),
              details: {
                component: <PhotoGallery photos={photos} avatar={image} setIsPhotoGalleryOpen={setIsPhotoGalleryOpen} />
              }
            }
          ]
        : []),
      { title: t('personalInformation'), details: personalInformation },
      { title: t('employmentDetails'), details: employmentDetails },
      { title: t('compliance'), details: employeeComplianceDataStatus },
      { title: t('fields'), details: otherFields },
      ...(modules?.help_desk && employeeId
        ? [
            {
              title: t('help_desk_tickets_history'),
              details: {
                component: (
                  <TicketsHistorySection
                    accountId={accountId}
                    employeeId={employeeId}
                    isActive={isActive}
                    isDeleted={isDeleted}
                    onCreateModalToggle={setTicketsHistoryCreateModal}
                    openCreatedTicketInSidePanel={inline}
                  />
                )
              }
            }
          ]
        : []),
      ...(employeeCommentsSortedData?.length > 0
        ? [
            {
              title: t('recentComments'),
              details: {
                component: (
                  <>
                    {employeeCommentsSortedData.map(
                      ({ id, comment, admin: commentAdmin, type, createdAt: commentCreatedAt }) => (
                        <Comment
                          id={id}
                          key={id}
                          dateFormat={dateFormat}
                          timeFormat={timeFormat}
                          t={t}
                          comment={comment}
                          createdAt={commentCreatedAt}
                          firstName={commentAdmin?.firstName || ''}
                          lastName={commentAdmin?.lastName || ''}
                          type={type}
                          showDelete={false}
                        />
                      )
                    )}
                  </>
                )
              }
            }
          ]
        : []),
      { title: t('wages'), details: [...baseWageDetails, ...positionsHourlyWages] }
    ];
  }, [
    getField,
    employeeIdField,
    level,
    accountConfig,
    t,
    inline,
    modules?.help_desk,
    employeeId,
    isActive,
    isDeleted,
    positions,
    jobSummary,
    phone,
    phoneSecondary,
    email,
    notes,
    branches,
    mappedDepartments,
    departmentId,
    mappedEmploymentTypes,
    employmentTypeId,
    fields,
    positionWages,
    wages,
    photos,
    image,
    employeeComplianceDataStatus,
    employeeCommentsSortedData,
    mappedPositions,
    dateFormat,
    showLabel,
    mappedLabels,
    timeFormat,
    mappedLocations,
    accountId,
    mappedBranches,
    formattingMap,
    mappedClientBranches,
    enableContractFeature
  ]);

  const allDetails = useMemo(() => [...extraSections, ...details], [extraSections, details]);

  const { updateEmployee } = useEmployee({ employeeId, enabled: false });

  const onRestoreEmployee = useCallback(async () => {
    await updateEmployee({ accountId, employeeId, isDeleted: false, invalidateEmployee: false });
    onClose();
  }, [accountId, employeeId, onClose, updateEmployee]);

  useEffect(() => {
    if (inline) return;
    attachModal({ onClose });

    return () => {
      detachModal({ onClose });
    };
    // ignore onClose as in most cases it's not memoized and causes infinite rerender
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attachModal, detachModal, inline]);

  const contextMenuOptions = useMemo(
    () => [
      {
        visible: !isDeleted && isActive && !!modules?.help_desk,
        label: t('help_desk_create_ticket'),
        icon: CreateTicketIcon,
        onClick: () => {
          trackCreateTicketOpened('Employee profile - more menu');
          setCreateTicketModal(true);
        }
      },
      {
        visible: !isDeleted && !!myPrivileges[privileges.EDIT_HR],
        label: t(isActive ? 'turnToNonActive' : 'turnToActive'),
        icon: isActive ? ActivateIcon : InActivateIcon,
        onClick: () => {
          if (isActive) {
            return setDeactivateEmployeeModal({
              employee: {
                firstName,
                lastName,
                id: employeeId
              },
              updateEmployee
            });
          }

          updateEmployee({ accountId, employeeId, isActive: !isActive });
          onClose();
        }
      },
      {
        visible: !!myPrivileges[privileges.DELETE_EMPLOYEE],
        label: isDeleted ? t('restoreEmployee') : t('deleteEmployee'),
        icon: DeleteIcon,
        onClick: () => (isDeleted ? onRestoreEmployee() : setDeleteEmployeeModal(true))
      }
    ],
    [
      t,
      firstName,
      myPrivileges,
      isDeleted,
      isActive,
      updateEmployee,
      accountId,
      employeeId,
      onClose,
      lastName,
      onRestoreEmployee,
      modules,
      trackCreateTicketOpened
    ]
  );

  const onCloseDeleteEmployeeModal = useCallback(() => {
    setDeleteEmployeeModal(false);
    onClose();
  }, [onClose]);

  const onCloseDeactivateEmployeeModal = useCallback(() => {
    setDeactivateEmployeeModal(false);
    onClose();
  }, [onClose]);

  const onCloseCreateTicketModal = useCallback(() => setCreateTicketModal(false), []);

  const openTicketInSidePanel = useOpenTicketInSidePanel();
  const onTicketCreated = useCallback(
    (ticket) => {
      queryClient.invalidateQueries(getHelpDeskEmployeeTicketsQueryKey(accountId, employeeId));
      setCreateTicketModal(false);
      openTicketInSidePanel(ticket?.id);
    },
    [queryClient, accountId, employeeId, openTicketInSidePanel]
  );

  if (isRestrictedFromEmployees) {
    return null;
  }

  const computedDefaultOpenRows =
    defaultOpenSections || allDetails.reduce((all, item, index) => ({ ...all, [index]: true }), {});

  const profileContent = (
    <>
      <ShortInfo>
        <Avatar firstName={firstName} lastName={lastName} image={image} size={160} />

        <NameRow>
          <Name>{getEmployeeDisplayName(firstName, lastName, isShowLastNameFirst)}</Name>
          {isPending && (
            <PendingWarning>
              <Tooltip body={t('pendingApproval')}>
                <CircleChip />
              </Tooltip>
            </PendingWarning>
          )}
        </NameRow>

        <RatingRow>
          <EmployeeKpis
            employeeId={employeeId}
            firstName={firstName}
            rating={rating}
            jobsRatingCount={jobsRatingCount}
          />
        </RatingRow>

        {(lastActivity || isPushNotification) && (
          <InfoRow>
            {lastActivity && (
              <LastActivity>
                {t('lastActivity')}: <TimeAgoRenderer {...lastActivityProps} />
              </LastActivity>
            )}
            {isPushNotification && (
              <Tooltip body={t('isPushNotification')}>
                <PushNotificationIcon />
              </Tooltip>
            )}
          </InfoRow>
        )}

        <Button secondary narrow onClick={onEdit}>
          {t('editEmployee')}
        </Button>

        <ActionsRow>
          {hasViewChatPrivilege && (
            <ActionCell>
              <CircleIcon
                tooltip={isAccountNetworkAccessSupplier ? t('disabledDirectWorkerChat') : t('chat')}
                isDisabled={isAccountNetworkAccessSupplier || !userId}
                hideBackground={isAccountNetworkAccessSupplier}
                onClick={() => {
                  onClose();
                  openUserChat(userId, accountId);
                }}>
                <ChatIcon $isDisabled={isAccountNetworkAccessSupplier} />
              </CircleIcon>

              <SmallBody $isDisabled={isAccountNetworkAccessSupplier}>{t('chat')} </SmallBody>
            </ActionCell>
          )}

          <ActionCell>
            <PromptClick body={t('areYouSureSendSMSInvite')} confirmText="send" onClick={() => inviteEmployee()}>
              <CircleIcon icon={InviteIcon} tooltip={t('inviteEmployee')} />
            </PromptClick>

            <SmallBody> {t('invite')} </SmallBody>
          </ActionCell>

          <ActionCell>
            <ContextMenu options={contextMenuOptions} />

            <SmallBody> {t('more')}</SmallBody>
          </ActionCell>
        </ActionsRow>
      </ShortInfo>

      {isLoading ? (
        <Loader />
      ) : (
        <Accordion defaultOpenRows={computedDefaultOpenRows}>
          {allDetails
            .filter((detailRow) => detailRow.details.component || detailRow.details.some?.((detail) => detail?.value))
            .map(({ title, details: innerDetails, fixedWidth }, index) => {
              return (
                <Accordion.Box title={title} key={index}>
                  {innerDetails.component}
                  {!innerDetails.component &&
                    innerDetails
                      .filter((detail) => detail?.value)
                      .map(
                        (
                          { label, value, showContextMenu, Icon, onClick, isCompliance, isInline: isInlineDetail },
                          idx
                        ) => (
                          <DetailRow
                            onClick={onClick}
                            key={idx}
                            label={label}
                            fixedWidth={fixedWidth}
                            value={value}
                            Icon={Icon}
                            options={showContextMenu && options}
                            isCompliance={isCompliance}
                            isInline={isInlineDetail}
                            isAttachment={title === t('fields')}
                            onPreviewOpenChange={setIsDocumentPreviewOpen}
                          />
                        )
                      )}
                </Accordion.Box>
              );
            })}
        </Accordion>
      )}
      {createTicketModal && (
        <CreateTicketModal
          lockedEmployeeId={employeeId}
          onClose={onCloseCreateTicketModal}
          onCreated={onTicketCreated}
        />
      )}

      {deleteEmployeeModal && (
        <DeleteEmployeeModal
          employee={{
            id: employeeId,
            firstName,
            lastName
          }}
          employeeFields={fieldsValues}
          updateEmployee={updateEmployee}
          onClose={onCloseDeleteEmployeeModal}
        />
      )}

      {deactivateEmployeeModal && (
        <DeactivateEmployeeModal
          employee={{
            id: employeeId,
            firstName,
            lastName
          }}
          updateEmployee={updateEmployee}
          onClose={onCloseDeactivateEmployeeModal}
        />
      )}
    </>
  );

  if (inline) {
    return profileContent;
  }

  return (
    <Portal>
      <Wrapper $isSingleView={isSingleView}>
        <Container ref={ref}>
          <CloseButton icon={CloseIcon} onClick={onClose} />
          {profileContent}
        </Container>
      </Wrapper>
    </Portal>
  );
};

export default ProfileSidebar;
